//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import { FaqSection } from '../../components/FaqSection'
import StarIcon from '@mui/icons-material/Star'
import { COLOR_TERTIARY, EXPAND_HERO_START, EXPAND_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    growOverseasHighlights,
    growOverseasChallenges,
    growOverseasSolutionStack,
    growOverseasSteps,
    growOverseasOutcomes,
    growOverseasQualifiers,
    growOverseasFaqs,
    growOverseasRelated,
} from '../../common/SolutionGrowOverseasContent'
// import { growOverseasTestimonials } from '../../common/Testimonials'

const C = COLOR_TERTIARY

export default function GrowOverseas() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Grow into Europe and Overseas"
                description="Expand your public sector pipeline beyond the UK. Monitor international procurement markets, adapt your bid content for different procurement cultures and get expert strategy support."
                canonical="/solutions/grow-into-europe-and-overseas"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">


                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Expanding Reach · International Growth" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18,
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Take Your Public Sector Success into{' '}
                                <Box component="span" sx={{ color: C }}>Europe & Overseas Markets</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                You've built a strong track record in the UK. Now you're ready to grow internationally, with the right tools and expert support alongside you
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${EXPAND_HERO_START} 0%, ${EXPAND_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {growOverseasHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three challenges that stop international expansion before it starts"
                subtitle="International procurement is a genuine opportunity, but each of these challenges has ended an expansion effort before it gained traction"
                items={growOverseasChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Three components built for international expansion: monitoring across markets, bid content adapted for overseas buyers and expert advisory support for your first international submissions"
                items={growOverseasSolutionStack}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />

            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="Your route into international markets"
                subtitle="Four steps from market mapping to your first international win, with the right support at every stage"
                steps={growOverseasSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After mapping your markets, assigning regional licences and working through the advisory phase, this is the position you'll be in"
                items={growOverseasOutcomes}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={growOverseasQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="The platform grows with you. Here's where most internationally-expanding businesses head next."
                items={growOverseasRelated}
                color={C}
            />


            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                subtitle="From businesses who've navigated international public sector expansion before you"
                items={growOverseasFaqs}
                color={DARK_800}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to take your public sector success international?"
                subtitle="Book a no-obligation discovery call. We'll map the right markets and approach for your business"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
